/**
 * plan-pago.ts — cronograma de pagos (2026-10-15).
 * The tesorero picks invoices to pay, builds a plan (cronograma), prints it for the bank, and on return confirms the
 * ones paid: each confirmed proveedor gets ONE Orden de Pago with all its invoices of the plan.
 *
 *   GET    /sumi/cuentas-pagar/planes-pago                       -> PlanPagoListResponse
 *   POST   /sumi/cuentas-pagar/planes-pago                       PlanPagoCreate     -> PlanPagoDetalle
 *   GET    /sumi/cuentas-pagar/planes-pago/:id                   -> PlanPagoDetalle
 *   GET    /sumi/cuentas-pagar/planes-pago/:id/cronograma.pdf    ?vista=detalle|consolidado (default detalle)
 *   PATCH  /sumi/cuentas-pagar/planes-pago/:id                   PlanPagoUpdate     -> PlanPagoDetalle
 *   DELETE /sumi/cuentas-pagar/planes-pago/:id                   (only without paid rows)
 *   POST   /sumi/cuentas-pagar/planes-pago/:id/facturas          PlanPagoAgregar    -> PlanPagoDetalle
 *   DELETE /sumi/cuentas-pagar/planes-pago/:id/facturas/:filaId  -> PlanPagoDetalle
 *   POST   /sumi/cuentas-pagar/planes-pago/:id/confirmar         PlanPagoConfirmar  -> PlanPagoConfirmarResponse
 *
 * The backend computes every amount (retentions from each supplier's own rates, the ARS conversion with the plan's
 * tipo de cambio). Money in responses is a decimal string; `*Ars` amounts are pesos, `pendiente` is in the invoice
 * currency. An amount is `null` when a foreign invoice has no tipo de cambio yet.
 */
import { z } from 'zod';
import { CurrencyCodeSchema } from '@suite/contracts-kernel';
import { IsoDateSchema, MedioPagoSchema } from './vocab.js';

const ComprobanteIdsSchema = z
  .array(z.string().uuid())
  .min(1, 'Elegí al menos una factura.')
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Hay facturas repetidas.' });

export const PlanPagoCreateSchema = z.object({
  /** Defaults to today (Argentina). */
  fechaPrevista: IsoDateSchema.optional(),
  comprobanteIds: ComprobanteIdsSchema,
});
export type PlanPagoCreate = z.infer<typeof PlanPagoCreateSchema>;

export const PlanPagoUpdateSchema = z
  .object({
    fechaPrevista: IsoDateSchema.optional(),
    /** Dólar divisa for the plan's foreign invoices; `null` clears it. */
    tipoCambio: z.number().positive().nullable().optional(),
  })
  .refine((v) => v.fechaPrevista !== undefined || v.tipoCambio !== undefined, { message: 'Nada para cambiar.' });
export type PlanPagoUpdate = z.infer<typeof PlanPagoUpdateSchema>;

export const PlanPagoAgregarSchema = z.object({ comprobanteIds: ComprobanteIdsSchema });
export type PlanPagoAgregar = z.infer<typeof PlanPagoAgregarSchema>;

export const PlanPagoConfirmarSchema = z.object({
  /** The rows the bank actually paid; the rest stays in the plan. */
  filaIds: z
    .array(z.string().uuid())
    .min(1, 'Tildá al menos un pago.')
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Hay filas repetidas.' }),
  fechaPago: IsoDateSchema,
  medioPago: MedioPagoSchema,
});
export type PlanPagoConfirmar = z.infer<typeof PlanPagoConfirmarSchema>;

export const PlanPagoRetencionSchema = z.object({
  tipoRetencionId: z.string().uuid(),
  nombre: z.string(),
  /** The rate that decided the amount (the supplier's own, else the type's); `null` for a fixed or manual amount. */
  alicuota: z.number().nullable(),
  montoArs: z.string(),
});

export const PlanPagoFilaSchema = z.object({
  filaId: z.string().uuid(),
  comprobanteId: z.string().uuid(),
  /** `FAC A 0001-00000012` */
  comprobanteLabel: z.string(),
  fechaVencimiento: z.string().nullable(),
  moneda: CurrencyCodeSchema,
  /** Current pending, invoice currency. `0` = already paid or annulled elsewhere: it cannot be confirmed. */
  pendiente: z.string(),
  brutoArs: z.string().nullable(),
  /** Only the retentions with an amount > 0. */
  retenciones: z.array(PlanPagoRetencionSchema),
  retencionesArs: z.string(),
  netoArs: z.string().nullable(),
  pagable: z.boolean(),
});
export type PlanPagoFila = z.infer<typeof PlanPagoFilaSchema>;

const ArsTotalesSchema = z.object({
  brutoArs: z.string().nullable(),
  retencionesArs: z.string(),
  netoArs: z.string().nullable(),
});

export const PlanPagoProveedorSchema = ArsTotalesSchema.extend({
  proveedorId: z.string().uuid(),
  nombre: z.string(),
  cuit: z.string().nullable(),
  filas: z.array(PlanPagoFilaSchema),
});
export type PlanPagoProveedor = z.infer<typeof PlanPagoProveedorSchema>;

export const PlanPagoPagadaSchema = z.object({
  filaId: z.string().uuid(),
  comprobanteLabel: z.string(),
  proveedorNombre: z.string(),
  ordenPago: z.object({ id: z.string().uuid(), numero: z.number().int().positive() }),
});

export const PlanPagoDetalleSchema = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive(),
  fechaPrevista: z.string(),
  tipoCambio: z.string().nullable(),
  /** `true` while the plan holds a foreign invoice and has no tipo de cambio. */
  requiereTipoCambio: z.boolean(),
  /** Still to pay, one entry per proveedor (by name). */
  proveedores: z.array(PlanPagoProveedorSchema),
  totales: ArsTotalesSchema,
  /** Rows already confirmed, with the order that paid them. */
  pagadas: z.array(PlanPagoPagadaSchema),
});
export type PlanPagoDetalle = z.infer<typeof PlanPagoDetalleSchema>;

export const PlanPagoResumenSchema = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive(),
  fechaPrevista: z.string(),
  /** Rows still to pay / already paid. */
  pendientes: z.number().int().nonnegative(),
  pagadas: z.number().int().nonnegative(),
  proveedores: z.number().int().nonnegative(),
  /** Σ bruto of the rows still to pay, invoice amounts converted with the plan's TC; `null` when a TC is missing. */
  totalPendienteArs: z.string().nullable(),
  /** The invoices still to pay in this plan — "Facturas a pagar" marks them "Cronograma N°X". */
  comprobanteIdsPendientes: z.array(z.string().uuid()),
  /** Every invoice of the plan, paid or not (`FAC A 0001-00000012`) — the list's "N° de factura" filter. */
  facturas: z.array(z.string()),
  createdAt: z.string(),
});
export type PlanPagoResumen = z.infer<typeof PlanPagoResumenSchema>;

export const PlanPagoListResponseSchema = z.object({ items: z.array(PlanPagoResumenSchema) });
export type PlanPagoListResponse = z.infer<typeof PlanPagoListResponseSchema>;

export const PlanPagoConfirmarResponseSchema = z.object({
  ordenes: z.array(
    z.object({
      id: z.string().uuid(),
      numero: z.number().int().positive(),
      proveedorId: z.string().uuid(),
      pagoMovimientoId: z.string().uuid(),
      /** What left the bank, ARS. */
      totalPagadoArs: z.string(),
    }),
  ),
});
export type PlanPagoConfirmarResponse = z.infer<typeof PlanPagoConfirmarResponseSchema>;

/**
 * The cronograma PDF (`GET …/:id/cronograma.pdf?vista=`): `detalle` = one row per invoice with its retentions (the
 * contadora, control); `consolidado` = one line per proveedor with what to transfer (the bank).
 */
export const PlanPagoPdfVistaSchema = z.enum(['detalle', 'consolidado']);
export type PlanPagoPdfVista = z.infer<typeof PlanPagoPdfVistaSchema>;

// ---- The flat cronograma (screen and PDF): one row per invoice, one column per retention type. ----

/** `FAC A 0001-00000012` → `A 0001-00000012` (the column already says they are invoices); a nota de débito keeps its `ND`. */
export function numeroFacturaCorto(comprobanteLabel: string): string {
  return comprobanteLabel.replace(/^FAC\s+/, '');
}

const SIGLAS: Array<{ re: RegExp; sigla: string }> = [
  { re: /iibb|ingresos brutos/i, sigla: 'R IIBB' },
  { re: /ganancias/i, sigla: 'R GAN' },
  { re: /\biva\b/i, sigla: 'R IVA' },
  { re: /suss/i, sigla: 'R SUSS' },
];

export interface PlanPagoRetencionColumna {
  tipoRetencionId: string;
  /** Short header: `R IIBB`, `R GAN`, `R IVA`, `R SUSS`; another type keeps its own name. */
  sigla: string;
  nombre: string;
}

/** The retention columns of a plan: the types that withhold something in it, IIBB · GAN · IVA · SUSS first, then by name. */
export function planPagoRetencionColumnas(
  proveedores: ReadonlyArray<{ filas: ReadonlyArray<{ retenciones: ReadonlyArray<{ tipoRetencionId: string; nombre: string }> }> }>,
): PlanPagoRetencionColumna[] {
  const tipos = new Map<string, string>();
  for (const p of proveedores) for (const f of p.filas) for (const r of f.retenciones) tipos.set(r.tipoRetencionId, r.nombre);
  const orden = (nombre: string) => {
    const i = SIGLAS.findIndex((s) => s.re.test(nombre));
    return i === -1 ? SIGLAS.length : i;
  };
  return [...tipos]
    .map(([tipoRetencionId, nombre]) => ({ tipoRetencionId, nombre, sigla: SIGLAS.find((s) => s.re.test(nombre))?.sigla ?? nombre }))
    .sort((a, b) => orden(a.nombre) - orden(b.nombre) || a.nombre.localeCompare(b.nombre, 'es'));
}

/** `0.5` → `0,5 %` */
export function alicuotaTexto(alicuota: number): string {
  return `${String(alicuota).replace('.', ',')} %`;
}

/** `30672661397` → `30-67266139-7`; anything that is not 11 digits is printed as stored. */
export function cuitConGuiones(cuit: string | null): string | null {
  if (cuit === null) return null;
  const d = cuit.replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : cuit;
}
